import { safeLocalStorage } from '@/lib/safeStorage';
import type { CreateOrganizationRequest } from './api/types';

/**
 * The company as described on the signup's company pages, kept until the company
 * exists. Signup runs who's signing up → company → email code, and only once the
 * code is in is there a signed-in login that can create anything; the setup wizard reads this the moment
 * the owner is signed in, creates the company from it in the background and opens
 * straight on pricing. Cleared once the company is created.
 *
 * Lives in localStorage rather than the session so that confirming the email from
 * a link in a new tab still finds it. It is the owner's own browser and the
 * details are theirs; an EIN typed here is removed with the rest on creation.
 */
export const COMPANY_DRAFT_STORAGE_KEY = 'ruta_company_draft';

/** The contact email and terms timestamp come from the first step, copied in when the company is described. */
export type CompanyDraft = Omit<CreateOrganizationRequest, 'contactEmail'> & {
  contactEmail?: string;
};

export function rememberCompanyDraft(draft: CompanyDraft): void {
  if (!draft.organizationName?.trim()) return;
  safeLocalStorage.setItem(COMPANY_DRAFT_STORAGE_KEY, JSON.stringify(draft));
}

export function recalledCompanyDraft(): CompanyDraft | null {
  try {
    const raw = safeLocalStorage.getItem(COMPANY_DRAFT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CompanyDraft> | null;
    if (!parsed || typeof parsed !== 'object') return null;
    if (typeof parsed.organizationName !== 'string' || !parsed.organizationName.trim()) return null;
    return parsed as CompanyDraft;
  } catch {
    return null;
  }
}

export function clearCompanyDraft(): void {
  safeLocalStorage.removeItem(COMPANY_DRAFT_STORAGE_KEY);
}
